/**
 * Container for a single tappable pipeline row inside the all-pipelines drawer
 * (SE-5489).
 *
 * Owns the BOUNDED, LAZY per-board card-count fan-out: each rendered row runs
 * one lightweight `PipelineDrawerCardCount` query, gated on the drawer being
 * open (`skip: !isDrawerOpen`) so closed drawers never fetch. There is no
 * board-level count field, so the count is derived from the (bounded) edge
 * list — `hasNextPage` renders an "N+" label when the true count exceeds the
 * page. Fanning out one query per rendered row (rather than eagerly across all
 * boards) keeps the surface's cost proportional to what the user actually sees.
 * @module features/player-kanban/components/molecules/SeeAllPipelinesDrawer/PipelineDrawerRow/PipelineDrawerRowContainer
 */

import { memo, useCallback, useMemo } from "react";

import PipelineDrawerRowView from "./PipelineDrawerRowView";

import type { PipelineDrawerRow } from "@/features/player-kanban/hooks/useSeeAllPipelinesData";

import { usePipelineDrawerCardCountQuery } from "@/generated/graphql";
import { useAppLocale } from "@/i18n";
import {
  MEDIUM_DATE_OPTIONS,
  formatDate as formatIntlDate,
} from "@/utils/intlFormat";

/** Bounded page size for the per-board card-count fan-out. */
const CARD_COUNT_PAGE_SIZE = 100;

/** Props for the row container. */
export interface PipelineDrawerRowContainerProps {
  /** Row data. */
  readonly row: PipelineDrawerRow;
  /** Whether the drawer is open — gates the count query so closed drawers do not fetch. */
  readonly isDrawerOpen: boolean;
  /** Selection callback — receives the row id when tapped. */
  readonly onSelect: (id: string) => void;
}

/**
 * Formats an ISO timestamp into a compact "last activity" label. Returns a
 * dash for an empty/unparseable value so the meta line never renders a raw
 * "Invalid Date".
 * @param iso - ISO timestamp string.
 * @param locale - The active UI locale.
 * @returns A compact date label, or "—" when unavailable.
 */
export const formatLastActivity = (iso: string, locale: string): string => {
  if (!iso) return "—";
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "—";
  // SE-7966: was hardcoded "en-US".
  return formatIntlDate(ms, locale, MEDIUM_DATE_OPTIONS) || "—";
};

/**
 * Row container — resolves the bounded card count, formats last activity, and
 * wraps onPress with the row id.
 * @param root0 - Component props
 * @param root0.row - Row data
 * @param root0.isDrawerOpen - Drawer open state (gates the count query)
 * @param root0.onSelect - Selection callback (receives the row id)
 */
const PipelineDrawerRowContainer = ({
  row,
  isDrawerOpen,
  onSelect,
}: PipelineDrawerRowContainerProps) => {
  const locale = useAppLocale();
  const { data } = usePipelineDrawerCardCountQuery({
    variables: { kanbanBoardId: row.id, first: CARD_COUNT_PAGE_SIZE },
    skip: !isDrawerOpen,
    fetchPolicy: "cache-and-network",
  });

  const countLabel = useMemo((): string => {
    const connection = data?.listKanbanCards;
    if (!connection) return "—";
    const count = connection.edges.length;
    return connection.pageInfo.hasNextPage ? `${count}+` : `${count}`;
  }, [data]);

  const lastActivityLabel = useMemo(
    () => formatLastActivity(row.lastActivityAt, locale),
    [row.lastActivityAt, locale]
  );

  const handlePress = useCallback(() => {
    onSelect(row.id);
  }, [onSelect, row.id]);

  return (
    <PipelineDrawerRowView
      row={row}
      countLabel={countLabel}
      lastActivityLabel={lastActivityLabel}
      onPress={handlePress}
    />
  );
};

PipelineDrawerRowContainer.displayName = "PipelineDrawerRowContainer";

export default memo(PipelineDrawerRowContainer);
